'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';

import type { Service } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Paragraph } from '../ui/paragraph';
import { ArrowLink } from '../ui/arrow-link';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export function Services({ services }: { services: Service[] }) {
  return (
    <section
      className={cn(
        'custom-section',
        'bg-[#F2EDE6] pb-[50px] pt-[25px] sm:pb-16 sm:pt-12 lg:pb-[100px] lg:pt-[50px]',
      )}
    >
      <div className={cn('custom-container', 'grid grid-cols-1 gap-4 md:grid-cols-2 lg:gap-6')}>
        {services.map((service, index) => (
          <ServiceCard key={index} service={service} isLarge={false} reverse={index % 2 !== 0} />
        ))}
      </div>
    </section>
  );
}

function ServiceCard({
  service,
  isLarge,
  reverse,
}: {
  service: Service;
  isLarge?: boolean;
  reverse?: boolean;
}) {
  const { t } = useTranslations();

  const href = service?.slug.replace(/-3$/, '');

  if (isLarge) {
    return (
      <div
        className={cn(
          'relative flex w-full items-end justify-end overflow-hidden rounded-[16px] p-4 sm:p-5 lg:max-w-[700px] lg:p-6',
          'ea-service-card--large',
        )}
      >
        <Image
          src={service?.image?.url ?? '/images/placeholder.png'}
          alt={resolveImageAlt(service?.image, service?.name)}
          width={0}
          height={0}
          className="absolute inset-0 z-0 h-full w-full object-cover object-center"
          sizes="100vw"
        />
        <div className="relative z-10 flex w-full flex-col items-start justify-start gap-6 rounded-[16px] bg-white p-4 sm:p-5 lg:p-6">
          <div className="flex w-full flex-col items-start justify-start gap-4">
            <h3 className={cn('h3', 'font-medium tracking-tighter text-foreground')}>
              {service?.name}
            </h3>
            <Paragraph
              innerHtml={service?.short_description ?? ''}
              className="w-full"
              size="sm"
              color="foreground"
            />
          </div>
          <ArrowLink title={t('text792')} href={href} />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex w-full items-start justify-start overflow-hidden rounded-[16px] bg-white lg:max-h-max lg:min-h-[288px] lg:items-center lg:justify-between',
        reverse ? 'flex-col-reverse lg:flex-row-reverse' : 'flex-col lg:flex-row',
      )}
    >
      <div className="flex h-full w-full flex-col items-start justify-between gap-4 p-4 sm:p-5 lg:p-6">
        <div className="flex w-full flex-col items-start justify-start gap-4">
          <h2 className={cn('h4', 'font-medium tracking-tighter text-foreground')}>
            {service?.name}
          </h2>
          <Paragraph
            innerHtml={service?.short_description ?? ''}
            className="w-full"
            size="sm"
            color="foreground"
          />
        </div>
        <ArrowLink title={t('text792')} href={href} />
      </div>
      <Image
        src={service?.image?.url ?? '/images/placeholder.png'}
        alt={resolveImageAlt(service?.image, service?.name)}
        width={0}
        height={0}
        className="h-[176px] w-full shrink-0 object-cover object-center sm:h-[220px] md:h-[350px] lg:h-full lg:min-h-[288px] lg:max-w-[164px]"
        sizes="(min-width: 1024px) 164px, (min-width: 768px) 50vw, calc(100vw - 32px)"
      />
    </div>
  );
}
